'use client';

import { useAuth } from '@clerk/clerk-react';
import {
  InfiniteData,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';

import { apiClient } from '../api/client';
import { Member, MembersResponse } from './useOrganizationMembers';

interface UpdateMemberRoleRequest {
  memberId: string;
  role: 'admin' | 'member';
}

interface UpdateMemberRoleResponse {
  message: string;
  memberId: string;
  role: string;
}

/**
 * Hook for updating a member's role in the organization
 * PATCH /api/v1/organization/members/{memberId}
 *
 * Uses optimistic updates with rollback on error
 */
export function useUpdateMemberRole() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation<
    UpdateMemberRoleResponse,
    Error,
    UpdateMemberRoleRequest,
    { previousMembers: InfiniteData<MembersResponse> | undefined }
  >({
    mutationFn: async ({ memberId, role }) => {
      const token = await getToken();
      if (!token) {
        throw new Error('No authentication token available');
      }

      const { data, error, response } = await apiClient.PATCH(
        '/api/v1/organization/members/{memberId}',
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          params: {
            path: {
              memberId: memberId,
            },
          },
          body: {
            role,
          },
        }
      );

      if (error || !response.ok || !data) {
        throw new Error('Failed to update member role');
      }

      return {
        message: data.message,
        memberId: data.memberId,
        role: data.role,
      };
    },
    onMutate: async ({ memberId, role }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['organization-members'] });

      // Snapshot the previous value
      const previousMembers = queryClient.getQueryData<
        InfiniteData<MembersResponse>
      >(['organization-members']);

      // Optimistically swap the role wherever the member appears across loaded pages.
      queryClient.setQueryData<InfiniteData<MembersResponse>>(
        ['organization-members'],
        (old) => {
          if (!old) return old;
          return {
            ...old,
            pages: old.pages.map((page) => ({
              members: page.members.map((m: Member) =>
                m.id === memberId ? { ...m, role } : m
              ),
              count: page.count,
            })),
          };
        }
      );

      // Return a context object with the snapshotted value
      return { previousMembers };
    },
    onError: (_err, _variables, context) => {
      // Rollback to the previous value on error
      if (context?.previousMembers) {
        queryClient.setQueryData(
          ['organization-members'],
          context.previousMembers
        );
      }
    },
    onSettled: () => {
      // Always refetch after error or success
      queryClient.invalidateQueries({ queryKey: ['organization-members'] });
    },
  });
}
